import { useEffect, useRef, useState } from 'react'

import { useSignalLog } from '@/app/lib/signalLog'
import { nextDelay } from '@/app/lib/quant/backoff'
import {
  FEED_UNREADABLE_LINE,
  decideFeedAnswer,
  feedLogLine,
  normalizeFeedBody,
  nextPublishingOffStreak,
  normalizeFeedState,
  symbolsLabel,
  type QuantFeedState,
} from '@/app/lib/quant/feedView'
import { quantApi } from '@/app/lib/quant/quantApi'
import type { FeedState } from '@/app/lib/quant/types'

export type { QuantFeedState } from '@/app/lib/quant/feedView'

/** Spec 040: one read of `GET /quant/feed` every 10 s while the tab is visible. */
export const FEED_POLL_MS = 10_000

/**
 * Polls the market feed status. Call it ONCE (from `QuantShell`). A failed read
 * keeps the last answer on screen (`failed: true`); with nothing held the state
 * is `unavailable`. Retries back off 2, 4, 8, then 15 s; the first success
 * returns to 10 s. Spec 040 R-4: with an answer held, ONE `unavailable` answer with
 * `code: feed_unreachable` is treated like a failed read (held answer kept,
 * `failed: true`, failure back-off, no terminal line) and applied only when it is
 * the second such answer in a row; any other outcome, a transport failure
 * included, resets that count. The first answer after mount is always applied.
 * `publishingOffStreak` counts consecutive applied answers with publishing not ok.
 * Nothing is requested while the document is hidden, and a read
 * happens at once when it becomes visible again.
 */
export function useQuantFeed(): QuantFeedState {
  const [state, setState] = useState<QuantFeedState>({ status: 'loading' })
  const { emit } = useSignalLog()
  const emitRef = useRef(emit)
  useEffect(() => {
    emitRef.current = emit
  }, [emit])

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let inFlight = false
    let controller: AbortController | null = null
    let failures = 0
    let lastLogged: FeedState | null = null
    let held = false
    let unreachableStreak = 0
    let publishingOffStreak = 0

    const isHidden = () => document.visibilityState === 'hidden'
    const clearTimer = () => {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    }
    const schedule = (ms: number) => {
      clearTimer()
      if (cancelled || isHidden()) return
      timer = setTimeout(read, ms)
    }
    function read() {
      timer = null
      if (cancelled || inFlight || isHidden()) return
      inFlight = true
      const own = new AbortController()
      controller = own
      quantApi.getFeed(own.signal).then(
        (raw) => {
          if (cancelled || own.signal.aborted) return
          inFlight = false
          const feed = normalizeFeedBody(raw)
          const next = normalizeFeedState(feed.state)
          const decision = decideFeedAnswer({ held, unreachableStreak, state: next, code: feed.code })
          unreachableStreak = decision.unreachableStreak
          if (!decision.apply) {
            failures += 1
            setState((prev) => (prev.status === 'loaded' ? { ...prev, failed: true } : prev))
            schedule(nextDelay(failures))
            return
          }
          failures = 0
          held = true
          publishingOffStreak = nextPublishingOffStreak(publishingOffStreak, feed)
          const streak = publishingOffStreak
          setState({ status: 'loaded', feed, state: next, receivedAt: Date.now(), failed: false, publishingOffStreak: streak })
          const line = feedLogLine({ previous: lastLogged, next, symbolsText: symbolsLabel(feed.symbols) })
          if (line !== null) emitRef.current(line.tag, line.msg)
          lastLogged = next
          schedule(FEED_POLL_MS)
        },
        () => {
          if (cancelled || own.signal.aborted) return
          inFlight = false
          failures += 1
          unreachableStreak = 0
          if (failures === 1) emitRef.current(FEED_UNREADABLE_LINE.tag, FEED_UNREADABLE_LINE.msg)
          setState((prev) => (prev.status === 'loaded' ? { ...prev, failed: true } : { status: 'unavailable' }))
          schedule(nextDelay(failures))
        },
      )
    }
    const onVisibility = () => {
      if (cancelled) return
      if (isHidden()) {
        clearTimer()
      } else {
        clearTimer()
        read()
      }
    }

    document.addEventListener('visibilitychange', onVisibility)
    read()
    return () => {
      cancelled = true
      clearTimer()
      document.removeEventListener('visibilitychange', onVisibility)
      controller?.abort()
    }
  }, [])

  return state
}
